import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Ship, ArrowRight, Download, Ticket, Calendar } from "lucide-react";
import { format } from "date-fns";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import api, { inr } from "@/lib/api";

export default function Confirmation() {
  const { bookingId } = useParams();
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((r) => setB(r.data)).catch(() => {});
  }, [bookingId]);

  if (!b) return <div className="min-h-screen bg-background"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-12">
        <div className="text-center mb-8 animate-rise">
          <span className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" strokeWidth={1.75} />
          </span>
          <h1 className="font-display font-bold text-3xl text-primary tracking-tight">Booking confirmed!</h1>
          <p className="text-muted-foreground mt-2">Your e-ticket is ready. A copy has been saved to My Bookings.</p>
        </div>

        {/* Ticket */}
        <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-lg animate-rise" data-testid="eticket">
          <div className="bg-primary text-white px-6 py-5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-accent" />
              <span className="font-display font-semibold">WaveLine e-Ticket</span>
            </div>
            <span className="font-mono text-sm bg-white/10 px-3 py-1 rounded" data-testid="confirmation-ref">{b.booking_ref}</span>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
              <Ship className="h-3.5 w-3.5 text-accent" /> {b.ferry_name}
            </div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-display font-bold text-3xl text-primary">{b.departure_time}</div>
                <div className="text-sm text-muted-foreground">{b.from_port?.city}</div>
                <div className="text-xs text-muted-foreground">{b.from_port?.name}</div>
              </div>
              <ArrowRight className="h-6 w-6 text-accent" />
              <div className="text-right">
                <div className="font-display font-bold text-3xl text-primary">{b.arrival_time}</div>
                <div className="text-sm text-muted-foreground">{b.to_port?.city}</div>
                <div className="text-xs text-muted-foreground">{b.to_port?.name}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 border-t border-dashed border-border pt-5 text-sm">
              <div>
                <div className="overline text-muted-foreground mb-1">Date</div>
                <div className="flex items-center gap-1.5 font-medium text-primary">
                  <Calendar className="h-3.5 w-3.5 text-accent" />
                  {b.travel_date && format(new Date(b.travel_date), "dd MMM yyyy")}
                </div>
              </div>
              <div>
                <div className="overline text-muted-foreground mb-1">Class</div>
                <div className="font-medium text-primary capitalize">{b.travel_class}</div>
              </div>
              <div>
                <div className="overline text-muted-foreground mb-1">Paid</div>
                <div className="font-display font-bold text-primary">{inr(b.amount)}</div>
              </div>
            </div>
            <div className="border-t border-dashed border-border mt-5 pt-5">
              <div className="overline text-muted-foreground mb-2">Passengers</div>
              <ul className="space-y-1 text-sm">
                {b.passengers?.map((p, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="text-primary font-medium">{p.name}</span>
                    <span className="text-muted-foreground capitalize">{p.gender}, {p.age}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button variant="outline" className="flex-1" onClick={() => window.print()} data-testid="download-ticket-btn">
            <Download className="h-4 w-4 mr-2" /> Save / Print ticket
          </Button>
          <Button className="flex-1 bg-primary hover:bg-primary/90 text-white" asChild>
            <Link to="/my-bookings" data-testid="go-mybookings-btn">View my bookings</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
